import React from 'react';
import { VideoGenerationJob } from '../../services/api/videoGenerationService';
import { Clock, Loader2, CheckCircle, XCircle, ChevronRight, Video } from 'lucide-react';
import { videoFailureLabel } from './videoFailure';

/** バッジに出す動画モデル名。未知の値・未設定は Gemini 扱い（従来の分岐と同じ）。 */
const VIDEO_MODEL_LABELS: Record<string, string> = {
  seedance: 'Seedance 1.5',
  'seedance-2-5': 'Seedance 2.5',
  'seedance-2-pro': 'Seedance 2.0 Pro',
  'seedance-2-fast': 'Seedance 2.0 Fast',
  'seedance-2-mini': 'Seedance 2.0 Mini',
  'minimax-h3': 'MiniMax H3',
  'fal-h3-max': 'H3 Max (fal.ai)',
  'fal-h3-max-turbo': 'H3 Max Turbo (fal.ai)',
};

interface GeneratingVideoJobCardProps {
  job: VideoGenerationJob;
  onClick: () => void;
  progressStep?: string;
  progressPercentage?: number;
}

const GeneratingVideoJobCard: React.FC<GeneratingVideoJobCardProps> = ({ job, onClick, progressStep, progressPercentage }) => {
  const getStatusIcon = () => {
    switch (job.status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStepLabel = (step?: string): string => {
    switch (step) {
      case 'initializing':
        return '初期化中...';
      case 'optimizing_prompt':
        return 'プロンプト最適化中...';
      case 'generating':
        return '動画生成中...';
      case 'downloading':
        return 'ダウンロード中...';
      case 'uploading':
        return 'アップロード中...';
      case 'saving':
        return '保存中...';
      case 'completed':
        return '完了';
      default:
        return '';
    }
  };

  const getStatusText = () => {
    if (job.status === 'processing' && (progressStep || (job as any).progress_step)) {
      return getStepLabel(progressStep || (job as any).progress_step);
    }
    switch (job.status) {
      case 'pending':
        return '待機中';
      case 'processing':
        return '動画生成中...';
      case 'completed':
        return '完了';
      case 'failed': {
        // 「失敗」だけでは何を直せば通るのか分からず、モーダルを開かせていた。
        const label = videoFailureLabel(job.error_code);
        return label ? `失敗 · ${label}` : '失敗';
      }
      default:
        return '';
    }
  };

  const getProgressBarColor = () => {
    switch (job.status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getProgress = () => {
    if (progressPercentage !== undefined) {
      return progressPercentage;
    }
    // Fallback
    switch (job.status) {
      case 'pending':
        return 0;
      case 'processing':
        return (job as any).progress_percentage || 50;
      case 'completed':
        return 100;
      case 'failed':
        return 100;
      default:
        return 0;
    }
  };

  const truncatePrompt = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getAspectRatioLabel = (aspectRatio: string) => {
    switch (aspectRatio) {
      case '16:9':
        return '横長 (16:9)';
      case '9:16':
        return '縦長 (9:16)';
      case '1:1':
        return '正方形 (1:1)';
      default:
        return aspectRatio;
    }
  };

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm font-medium text-gray-700">{getStatusText()}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor()}`}
          style={{ width: `${getProgress()}%` }}
        />
      </div>
      {job.status === 'processing' && (
        <p className="text-xs text-gray-400 mb-2">{getProgress()}%</p>
      )}

      {/* Prompt Preview */}
      <p className="text-sm text-gray-600 mb-2">{truncatePrompt(job.prompt)}</p>

      {/* Meta Info */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            <Video className="w-3 h-3" />
            {getAspectRatioLabel(job.aspect_ratio)}
          </span>
          <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
            {VIDEO_MODEL_LABELS[job.video_model ?? ''] ?? 'Gemini'}
          </span>
        </div>
        <span>
          {new Date(job.created_at).toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>
    </div>
  );
};

export default GeneratingVideoJobCard;
